import { memo } from 'react'
import { Edit } from 'lucide-react'

import { ListCardTrailingActionsFooter } from '@/components/layout/list-card-trailing-actions'
import { PersonListCard } from '@/components/person-list-card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'
import type { AdminUser } from '@/hooks/use-admin-users'
import {
  AdminUserAccessBadges,
  AdminUserAccountDates,
  AdminUserGroupBadges,
  adminUserToPersonModel,
} from '@/lib/admin-user-display'

type AdminUserCardProps = {
  user: AdminUser
  highlighted: boolean
  onUserClick: (userId: string) => void
  onEditUser: (userId: string, user: AdminUser) => void
  selected?: boolean
  onToggleSelect?: (userId: string) => void
}

const AdminUserCard = memo(function AdminUserCard({
  user,
  highlighted,
  onUserClick,
  onEditUser,
  selected,
  onToggleSelect,
}: AdminUserCardProps) {
  const person = adminUserToPersonModel(user)

  return (
    <PersonListCard
      variant="standard"
      className={cn(
        'h-full',
        highlighted && 'animate-pulse ring-2 ring-primary ring-offset-2',
      )}
      person={person}
      onOpen={() => onUserClick(user.id)}
      openAriaLabel={`Open user ${person.name}`}
      headerAside={
        <div className="flex items-start gap-2">
          <AdminUserAccessBadges user={user} align="end" />
          {onToggleSelect ? (
            <Checkbox
              checked={Boolean(selected)}
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={() => onToggleSelect(user.id)}
              aria-label={`Select ${person.name}`}
            />
          ) : null}
        </div>
      }
      body={
        <div className="space-y-4">
          <div>
            <div className="mb-1 text-xs font-medium text-muted-foreground">Groups</div>
            <AdminUserGroupBadges groups={user.groups} />
          </div>
          <AdminUserAccountDates user={user} />
        </div>
      }
      footer={
        <ListCardTrailingActionsFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onEditUser(user.id, user)
            }}
          >
            <Edit className="mr-1 size-4" aria-hidden />
            Edit
          </Button>
        </ListCardTrailingActionsFooter>
      }
    />
  )
})

export { AdminUserCard }
